import { devLog } from "../utils/devLog";
/**
 * Redirect URL Builder Utility
 * Provides methods to construct redirect URLs with transaction parameters
 * Following SaaS standards for URL parameter handling and encoding
 */

/**
 * Ensure a URL string has an http(s) scheme before handing it to `new URL()`
 * or `window.open()` / `window.location.href`.
 *
 * Merchants frequently configure `checkoutRedirectUrl` as a bare domain
 * (e.g. `www.altruon.io`), which the URL constructor rejects as invalid and
 * which the browser treats as a same-origin relative path when used with
 * `window.open` / `window.location.href`. Normalizing once at the boundary
 * prevents every gateway integration from having to re-implement this.
 *
 * @param {string} rawUrl
 * @returns {string} URL guaranteed to start with `http://` or `https://`
 */
export const normalizeUrlProtocol = (rawUrl) => {
  if (typeof rawUrl !== 'string') return rawUrl;
  const trimmed = rawUrl.trim();
  if (!trimmed) return trimmed;
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) return trimmed;
  const protocol = /localhost|127\.0\.0\.1/i.test(trimmed) ? 'http://' : 'https://';
  return `${protocol}${trimmed.replace(/^\/+/, '')}`;
};

/**
 * Safe drop-in replacement for `window.open(url, ...)` / `window.location.href = url`
 * that normalizes the scheme first and swallows the call when the URL is blank.
 *
 * @param {string} rawUrl
 * @param {Object} [options]
 * @param {"self" | "blank"} [options.target="self"] - "self" navigates the current
 *   tab via `window.location.href`; "blank" opens a new tab with noopener.
 * @returns {boolean} true if a navigation was initiated
 */
export const safeRedirect = (rawUrl, { target = 'self' } = {}) => {
  const normalized = normalizeUrlProtocol(rawUrl);
  if (!normalized) {
    console.warn('safeRedirect: no URL provided, skipping navigation');
    return false;
  }
  if (target === 'blank') {
    window.open(normalized, '_blank', 'noopener,noreferrer');
  } else {
    window.location.href = normalized;
  }
  return true;
};

/**
 * Builds a complete redirect URL with transaction parameters
 * 
 * @param {Object} options - Configuration object
 * @param {string} options.primaryUrl - Primary redirect URL (e.g., hosted_page_redirect from backend)
 * @param {string} options.fallbackUrl - Fallback redirect URL (e.g., checkoutRedirectUrl from config)
 * @param {string} options.transactionId - Transaction/Order ID
 * @param {string|boolean} options.status - Transaction status (success, failed, pending, true/false)
 * @returns {string} - Complete redirect URL with parameters
 * @throws {Error} - If no valid URL is provided
 * 
 * @note If the URL already contains trxId or status parameters, they will not be appended again
 */
export const buildRedirectUrl = ({ 
  primaryUrl, 
  fallbackUrl, 
  transactionId, 
  status 
} = {}) => {
  // Use primary URL if available, otherwise fall back to secondary URL
  devLog('The status being passed to buildRedirectUrl is:', status);
  const baseUrl = primaryUrl || fallbackUrl;
  
  if (!baseUrl) {
    console.warn('No redirect URL provided. Unable to build redirect URL.');
    return null;
  }

  try {
    // Merchants often save a `checkoutRedirectUrl` without a protocol
    // (e.g. "www.altruon.io"). `new URL(...)` throws "Invalid URL" in that
    // case, so prepend a scheme defensively. Localhost stays on http so dev
    // environments keep working.
    const normalizedBaseUrl = normalizeUrlProtocol(baseUrl);

    // Parse the URL
    const url = new URL(normalizedBaseUrl);
    
    // Only append transaction parameters if they don't already exist in the URL
    if (transactionId && !url.searchParams.has('trx_id')) {
      url.searchParams.set('trx_id', String(transactionId).trim());
    }
    
    if (status !== null && status !== undefined && !url.searchParams.has('status')) {
      // Normalize status value
      const normalizedStatus = normalizeStatus(status);
      url.searchParams.set('status', normalizedStatus);
    }
    
    return url.toString();
  } catch (error) {
    console.error('Invalid redirect URL:', error);
    return null;
  }
};

/**
 * Normalizes transaction status to standard format
 * @param {string|boolean} status - Raw status value
 * @returns {string} - Normalized status (success, failed, pending)
 */
const normalizeStatus = (status) => {
  if (typeof status === 'boolean') {
    return status ? 'success' : 'failed';
  }
  
  const statusStr = String(status).toLowerCase().trim();
  
  // Map common status values
  const statusMap = {
    'success': 'success',
    'successful': 'success',
    'completed': 'success',
    'true': 'success',
    'failed': 'failed',
    'failure': 'failed',
    'false': 'failed',
    'pending': 'pending',
    'processing': 'pending',
  };
  
  return statusMap[statusStr] || statusStr;
};

/**
 * Validates if a URL string is properly formatted
 * @param {string} urlString - URL to validate
 * @returns {boolean} - True if valid URL
 */
export const isValidUrl = (urlString) => {
  if (!urlString || typeof urlString !== 'string') {
    return false;
  }
  
  try {
    new URL(normalizeUrlProtocol(urlString));
    return true;
  } catch {
    return false;
  }
};

/**
 * Extracts transaction parameters from a URL
 * @param {string} urlString - URL containing transaction parameters
 * @returns {Object} - Object with trxId and status
 */
export const extractTransactionParams = (urlString) => {
  if (!urlString || !isValidUrl(urlString)) {
    return { trx_id: null, status: null };
  }
  
  try {
    const url = new URL(normalizeUrlProtocol(urlString));
    return {
      trx_id: url.searchParams.get('trx_id'),
      status: url.searchParams.get('status'),
    };
  } catch {
    return { trx_id: null, status: null };
  }
};

/**
 * First non-blank redirect candidate. Use for merchant return URLs so UI never
 * depends on only catalog DTO when `/conf` (CheckoutContext) already has one.
 *
 * @param {...unknown} candidates
 * @returns {string|null}
 */
export const pickRedirectUrl = (...candidates) => {
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
  }
  return null;
};

/**
 * Gets the appropriate redirect URL for a transaction
 * Prioritizes backend-provided hosted_page_redirect, falls back to config
 * 
 * @param {Object} response - API response object from backend
 * @param {string} response.hosted_page_redirect - Redirect URL from backend
 * @param {Object} configDto - Configuration DTO object
 * @param {string} configDto.checkoutRedirectUrl - Default redirect URL from config
 * @param {string} [tenantRedirectUrl] - Domain `/conf` redirect (CheckoutContext)
 * @returns {string} - Final redirect URL to use
 */
export const getRedirectUrl = (response = {}, configDto = {}, tenantRedirectUrl) => {
  return pickRedirectUrl(
    response?.hosted_page_redirect,
    configDto?.checkoutRedirectUrl,
    tenantRedirectUrl
  );
};

/**
 * Builds transaction redirect data ready for component consumption
 * @param {Object} options - Configuration
 * @returns {Object} - Transaction redirect data
 */
export const buildTransactionRedirectData = ({
  apiResponse = {},
  configDto = {},
  transactionId = '',
  status = null,
} = {}) => {
  const redirectUrl = getRedirectUrl(apiResponse, configDto);
  
  return {
    redirectUrl,
    finalRedirectUrl: buildRedirectUrl({
      primaryUrl: apiResponse?.hosted_page_redirect,
      fallbackUrl: configDto?.checkoutRedirectUrl,
      transactionId,
      status,
    }),
    hasBackendRedirect: !!apiResponse?.hosted_page_redirect,
    hasFallbackRedirect: !!configDto?.checkoutRedirectUrl,
  };
};

export default {
  buildRedirectUrl,
  isValidUrl,
  extractTransactionParams,
  pickRedirectUrl,
  getRedirectUrl,
  buildTransactionRedirectData,
  normalizeUrlProtocol,
  safeRedirect,
};
